import React from 'react';
import { useTemplate } from '../../hooks/useTemplate';
import { TemplateCard } from './TemplateCard';
import type { TemplateId } from '../../types';

export function TemplateSelector() {
  const { templates, activeTemplateId, selectTemplate, isLoading, error } = useTemplate();

  return (
    <section className="flex flex-col h-full" aria-label="Template selector">
      {/* Panel header */}
      <div className="px-4 py-3 border-b border-zinc-700 shrink-0">
        <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
          Templates
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col gap-2.5" aria-busy="true" aria-label="Loading templates">
            {[1, 2, 3].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div
            role="alert"
            className="rounded-lg border border-red-800 bg-red-950/40 px-3 py-2.5 text-sm text-red-300"
          >
            <p className="font-medium mb-0.5">Failed to load templates</p>
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        {/* Template list */}
        {!isLoading && !error && templates.length === 0 && (
          <p className="text-sm text-zinc-500 text-center py-8">No templates found.</p>
        )}

        {!isLoading &&
          !error &&
          templates.map((template) => (
            <TemplateCard
              key={template.metadata.id}
              template={template}
              isSelected={template.metadata.id === activeTemplateId}
              onSelect={() => selectTemplate(template.metadata.id as TemplateId)}
            />
          ))}
      </div>
    </section>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-lg border-2 border-zinc-700 bg-zinc-800 p-3 animate-pulse">
      <div className="aspect-video w-full rounded-md bg-zinc-700 mb-3" />
      <div className="h-3.5 w-3/4 rounded bg-zinc-700 mb-2" />
      <div className="h-2.5 w-full rounded bg-zinc-700/70 mb-1" />
      <div className="h-2.5 w-2/3 rounded bg-zinc-700/70 mb-2.5" />
      <div className="flex gap-1.5">
        <div className="h-4 w-16 rounded bg-zinc-700" />
        <div className="h-4 w-10 rounded bg-zinc-700" />
        <div className="h-4 w-10 rounded bg-zinc-700" />
      </div>
    </div>
  );
}
